import type {DynamoEnvValueType} from '../types';
import {TypeImplementation, withSystem, EnvTypes} from '@packages/systems/wfdl';
import {functionOf, TText} from '@packages/systems/wfdl-ast';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {TOption} from '@packages/systems/plugins/Option';
import {TBasicLink} from '../Link';
import {TDynamoFileRef} from '@packages/systems/plugins-utils/DynamoGateway';
import {LinkTargetValue, TLinkTarget} from '../toBasicLinkV2Shared';

export const FileRefType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('FileRef'))),
});

// GraphQL file type
// https://github.com/webflow/webflow/blob/dev/public/js/designer-flux/test/components/render/fixtures/introspectionResponse.js#L584
type File = {
  url: string;
  file: GenericFile;
};

type GenericFile = {
  size: number;
  origFileName: string;
  createdOn: Date;
  updatedOn: Date;
  mimeType: string;
};

type FileLinkLiteral = {
  mode: 'file';
  assetUrl: string;
  target?: '_blank';
};

export const dynamoFileRefToText: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoFileRef)),
      TText
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<unknown>): string => {
    if (option === null || option.tag === 'None') return '';
    const {val} = option;
    if (
      val !== null &&
      typeof val === 'object' &&
      'url' in val &&
      typeof val.url === 'string'
    ) {
      return val.url;
    }
    return '';
  },
};

/**
 * @deprecated - replaced by the V2 implicit below
 *
 * Should be exported as hidden to ensure backwards compatibility before
 * we have run the site data migration to remove calls to this function
 * from element data.
 *
 * Should be deleted after the migration has been shipped.
 */
export const dynamoFileRefToBasicLink = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoFileRef)),
      TBasicLink
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<File>): FileLinkLiteral => {
    if (option === null || option.tag === 'None' || !('url' in option.val)) {
      return {
        assetUrl: '',
        mode: 'file',
      };
    }

    return {
      assetUrl: option.val.url,
      mode: 'file',
    };
  },
} as const;

export const dynamoFileRefToBasicLinkV2: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'File link settings'},
      functionOf(
        TLinkTarget,
        functionOf(
          withSystem({isBindable: true}, TOption(TDynamoFileRef)),
          TBasicLink
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (target: LinkTargetValue) => {
    return (option: DynamoEnvValueType<File>): FileLinkLiteral => {
      const openInNewTabBool = target.tag === 'New tab';
      if (
        option === null ||
        option.tag === 'None' ||
        !('url' in option.val) ||
        !option.val.url
      ) {
        return {
          assetUrl: '',
          mode: 'file',
        };
      }

      return {
        assetUrl: option.val.url,
        mode: 'file',
        ...(openInNewTabBool ? {target: '_blank'} : {}),
      };
    };
  },
};
